/**
 * Sign-in — two steps, because the API is two calls.
 *
 * POST /auth/login returns 200 with { status: 'mfa_required' } and sets a
 * short-lived pending cookie. That 200 is NOT a sign-in. The session only
 * exists after /auth/mfa/verify (or /auth/mfa/recovery) succeeds, which is
 * why this component branches on `status` rather than on response.ok.
 *
 * REQUIRE_MFA is true, so there is no path past the second step. A lost
 * authenticator needs a recovery code — hence the fallback link, which is
 * not an edge case worth hiding.
 */

import { useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ApiError, api } from '../api.js';
import { useAuth } from '../auth/AuthProvider.js';

type Step = 'password' | 'totp' | 'recovery';

/** Server error codes rendered as something an operator can act on. */
function messageFor(error: ApiError): string {
  switch (error.code) {
    case 'invalid_credentials':
      return 'That email and password combination was not recognized.';
    case 'invalid_code':
      return 'That code was not accepted. Codes expire every 30 seconds — wait for the next one.';
    case 'no_pending_authentication':
      return 'This sign-in attempt expired. Start again with your password.';
    // Every business this sign-in belongs to is closed (C-18, migration 0074).
    case 'businesses_closed':
      return 'Every business this sign-in belongs to has been closed. To ask about one, write to trey@technestack.com.';
    case 'mfa_not_enrolled':
      return 'This account has no authenticator enrolled. Run the enroll-totp CLI before signing in.';
    case 'rate_limited':
      return error.retryAfterSeconds
        ? `Too many attempts. Try again in ${Math.ceil(error.retryAfterSeconds / 60)} minute(s).`
        : 'Too many attempts. Try again shortly.';
    case 'network_unreachable':
      return 'Cannot reach the API. Check that it is running and that its ALLOWED_ORIGIN matches this address.';
    case 'invalid_request':
      return 'That code is not the right length.';
    default:
      return `Sign-in failed (${error.code}).`;
  }
}

export function LoginPage() {
  const { status, refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [step, setStep] = useState<Step>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (status === 'authenticated') {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from ?? '/'} replace />;
  }

  async function run(fn: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (caught) {
      setError(caught instanceof ApiError ? messageFor(caught) : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  }

  const submitPassword = (event: FormEvent) => {
    event.preventDefault();
    void run(async () => {
      const result = await api.login(email, password);
      // Branch on status, never on the status code.
      if (result.status === 'authenticated') {
        await refresh();
        navigate('/', { replace: true });
        return;
      }
      setPassword('');
      setStep('totp');
    });
  };

  const submitCode = (event: FormEvent) => {
    event.preventDefault();
    void run(async () => {
      if (step === 'recovery') await api.verifyRecoveryCode(code.trim());
      else await api.verifyTotp(code.trim());
      await refresh();
      navigate('/', { replace: true });
    });
  };

  return (
    <main className="centered">
      <h1>Techne Books</h1>

      {step === 'password' ? (
        <form onSubmit={submitPassword}>
          <label htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={busy}
          />

          <label htmlFor="password">Password</label>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={busy}
          />

          {error && <p role="alert" className="error">{error}</p>}

          <button type="submit" disabled={busy}>
            {busy ? 'Checking…' : 'Continue'}
          </button>
        </form>
      ) : (
        <form onSubmit={submitCode}>
          <p className="muted">
            {step === 'totp'
              ? 'Enter the current code from your authenticator.'
              : 'Enter one of your recovery codes. Each works once.'}
          </p>

          <label htmlFor="code">{step === 'totp' ? 'Six-digit code' : 'Recovery code'}</label>
          <input
            id="code"
            // `inputMode` rather than type=number: leading zeros survive and
            // no spinner appears on a value that is not a quantity.
            inputMode={step === 'totp' ? 'numeric' : 'text'}
            autoComplete="one-time-code"
            autoFocus
            required
            value={code}
            onChange={(e) => setCode(e.target.value)}
            disabled={busy}
          />

          {error && <p role="alert" className="error">{error}</p>}

          <button type="submit" disabled={busy}>
            {busy ? 'Verifying…' : 'Sign in'}
          </button>

          <div className="row">
            <button
              type="button"
              className="link"
              onClick={() => {
                setStep(step === 'totp' ? 'recovery' : 'totp');
                setCode('');
                setError(null);
              }}
            >
              {step === 'totp' ? 'Use a recovery code' : 'Use your authenticator'}
            </button>
            <button
              type="button"
              className="link"
              onClick={() => {
                setStep('password');
                setCode('');
                setError(null);
              }}
            >
              Start over
            </button>
          </div>
        </form>
      )}

      <p className="muted">
        <a href="/privacy">Privacy Policy</a>
      </p>
    </main>
  );
}
